import { FC, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import { WorkflowRuleDto } from 'src/generated/services/TFinancialApi';
import { RhfMuiSelect } from 'src/presentations/components/molecules/fields/rhf-mui-fields/RhfMuiSelect';
import Button from 'src/presentations/components/atom/button';
import { useUpsertWorkflowRule } from 'src/use-cases/settings/useWorkflowRules';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { FormContainer } from 'src/presentations/components/organisms/forms/FormContainer';
import { WorkflowTextField, WorkflowNumericField, WorkflowTextArea } from './WorkflowRuleFields';

interface WorkflowRuleDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workflowRule?: WorkflowRuleDto | null;
  onSuccess?: () => void;
}

// Options for select fields based on the WorkflowRuleDto interface

const currentWorkflowOptions = [
  { id: 'OPERATION_REVIEW', label: 'Operation Review' },
  { id: 'PROCUREMENT_REVIEW', label: 'Procurement Review' },
  { id: 'PENDING_REVIEW', label: 'Pending Review' },
  { id: 'DISPUTE_REVIEW', label: 'Dispute Review' },
  { id: 'PROCUREMENT_NO_CONTRACT_REVIEW', label: 'Procurement No Contract Review' },
  { id: 'FINANCE_REVIEW', label: 'Finance Review' },
];

const workflowActionOptions = [
  { id: 'SEND_TO_OPERATION', label: 'Send to Operation' },
  { id: 'SEND_TO_PROCUREMENT', label: 'Send to Procurement' },
  { id: 'SEND_TO_FINANCE', label: 'Send to Finance' },
  { id: 'START_DISPUTE', label: 'Start Dispute' },
  { id: 'CANCEL_DISPUTE', label: 'Cancel Dispute' },
  { id: 'MARK_INVOICE_TO_REJECT', label: 'Mark Invoice to Reject' },
  { id: 'MARK_INVOICE_TO_APPROVE', label: 'Mark Invoice to Approve' },
  { id: 'ACCEPT_CREDIT_NOTE', label: 'Accept Credit Note' },
  { id: 'ACCEPT_APPROVAL', label: 'Accept Approval' },
  { id: 'READY_FOR_PAYMENT', label: 'Ready for Payment' },
  { id: 'REJECT_INVOICE', label: 'Reject Invoice' },
  { id: 'ACCEPT_INVOICE', label: 'Accept Invoice' },
  { id: 'ACCEPT_AS_SPOT_SHIPMENT', label: 'Accept as Spot Shipment' },
];

const nextWorkflowOptions = [
  { id: 'OPERATION_REVIEW', label: 'Operation Review' },
  { id: 'PROCUREMENT_REVIEW', label: 'Procurement Review' },
  { id: 'PENDING_REVIEW', label: 'Pending Review' },
  { id: 'DISPUTE_REVIEW', label: 'Dispute Review' },
  { id: 'PROCUREMENT_NO_CONTRACT_REVIEW', label: 'Procurement No Contract Review' },
  { id: 'FINANCE_REVIEW', label: 'Finance Review' },
];

const nextInvoiceStatusOptions = [
  { id: '__EMPTY__', label: 'Empty' },
  { id: 'FLAGGED', label: 'Flagged' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'TO_APPROVE', label: 'To Approve' },
  { id: 'PENDING_DOCUMENT', label: 'Pending Document' },
  { id: 'REJECTED', label: 'Rejected' },
  { id: 'TO_REJECT', label: 'To Reject' },
  { id: 'PARTIAL', label: 'Partial' },
];

const nextShipmentStatusOptions = [
  { id: '__EMPTY__', label: 'Empty' },
  { id: 'FLAGGED', label: 'Flagged' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'SPOT_SHIPMENT', label: 'Spot Shipment' },
  { id: 'REJECTED', label: 'Rejected' },
  { id: 'UNDER_REVIEW', label: 'Under Review' },
];

export const WorkflowRuleDrawer: FC<WorkflowRuleDrawerProps> = (props) => {
  const { isOpen, onClose, workflowRule, onSuccess } = props;
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutateAsync: upsertWorkflowRule, isLoading } = useUpsertWorkflowRule();

  const methods = useForm<WorkflowRuleDto>({
    defaultValues: {
      description: '',
      type: 'INVOICE',
      workflowAction: 'SEND_TO_OPERATION',
      currentWorkflow: undefined,
      nextWorkflow: undefined,
      nextInvoiceStatus: 'TO_APPROVE',
      nextShipmentStatus: 'UNDER_REVIEW',
      postAction: '',
      activityLogType: '',
      priority: undefined,
      version: '',
      condition: '',
      userRoles: [],
    },
  });

  const { reset, handleSubmit } = methods;

  // Helper function to normalize userRoles to always be an array
  const normalizeUserRoles = useCallback((roles: any): string[] => {
    if (Array.isArray(roles)) return roles;
    if (roles) return [roles];
    return [];
  }, []);

  // Reset form when drawer opens or workflowRule changes
  useEffect(() => {
    if (isOpen && workflowRule) {
      // Ensure userRoles is always an array
      const normalizedRule = {
        ...workflowRule,
        userRoles: normalizeUserRoles(workflowRule.userRoles),
      };
      reset(normalizedRule);
      setIsEditMode(false);
    } else if (isOpen && !workflowRule) {
      reset({
        description: '',
        type: 'INVOICE',
        workflowAction: 'SEND_TO_OPERATION',
        currentWorkflow: undefined,
        nextWorkflow: undefined,
        nextInvoiceStatus: 'TO_APPROVE',
        nextShipmentStatus: 'UNDER_REVIEW',
        postAction: '',
        activityLogType: '',
        priority: undefined,
        version: '',
        condition: '',
        userRoles: [],
      });
      setIsEditMode(true); // New rule starts in edit mode
    }
  }, [isOpen, workflowRule, reset, normalizeUserRoles]);

  const onSubmit = async (data: WorkflowRuleDto) => {
    try {
      const payload = {
        ...data,
        userRoles: normalizeUserRoles(data.userRoles),
        nextInvoiceStatus: (data.nextInvoiceStatus as any) === '__EMPTY__' ? null : data.nextInvoiceStatus,
        nextShipmentStatus: (data.nextShipmentStatus as any) === '__EMPTY__' ? null : data.nextShipmentStatus,
      };

      await upsertWorkflowRule(payload);
      openToast({
        toastType: 'success',
        title: 'Success',
        description: `Workflow rule ${workflowRule ? 'updated' : 'created'} successfully!`,
      });
      setIsEditMode(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      openToastError(new Error('Failed to save workflow rule'));
    }
  };

  const handleCancel = () => {
    if (workflowRule) {
      // Ensure userRoles is always an array
      const normalizedRule = {
        ...workflowRule,
        userRoles: normalizeUserRoles(workflowRule.userRoles),
      };
      reset(normalizedRule);
      setIsEditMode(false);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  const isReadOnly = !isEditMode;

  return (
    <div className='fixed right-0 top-[80px] bottom-0 w-[560px] z-[990] bg-white shadow-[0px_4px_20px_rgba(0,0,0,0.25)]'>
      <div className='w-full h-full flex flex-col'>
        <div className='flex items-center justify-between px-24 py-20 border-b border-neutral-20'>
          <div>
            <h2 className='text-20 font-bold text-blue_grey-90'>{workflowRule?.description || 'New Workflow Rule'}</h2>
            {workflowRule?.id && <p className='text-12 text-neutral-60 mt-4'>Rule ID {workflowRule.id}</p>}
          </div>
          <div className='flex items-center gap-8'>
            {!isEditMode && workflowRule && (
              <IconButton onClick={() => setIsEditMode(true)} size='small'>
                <EditIcon fontSize='small' />
              </IconButton>
            )}
            <IconButton onClick={onClose} size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </div>
        </div>

        <div className='flex-1 overflow-y-auto px-24 py-20'>
          <FormContainer formContext={methods} isReadOnly={isReadOnly}>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-20'>
              <RhfMuiSelect
                name='currentWorkflow'
                label='Current Workflow'
                placeholder='Current Workflow'
                options={currentWorkflowOptions}
                disabled={isReadOnly}
              />

              <RhfMuiSelect
                name='workflowAction'
                label='Workflow Action'
                placeholder='Workflow Actio'
                options={workflowActionOptions}
                disabled={isReadOnly}
                required
              />

              <RhfMuiSelect name='nextWorkflow' label='Next Workflow' placeholder='Next Workflow' options={nextWorkflowOptions} disabled={isReadOnly} />

              <RhfMuiSelect
                name='nextInvoiceStatus'
                label='Next Invoice Status'
                placeholder='Next Invoice Status'
                options={nextInvoiceStatusOptions}
                disabled={isReadOnly}
              />

              <RhfMuiSelect
                name='nextShipmentStatus'
                label='Next Shipment Status'
                placeholder='Next Shipment Status'
                options={nextShipmentStatusOptions}
                disabled={isReadOnly}
              />

              <WorkflowTextField name='postAction' label='Post Action' placeholder='Free Text' disabled={isReadOnly} />

              <WorkflowTextField name='activityLogType' label='Activity Log Type' placeholder='Free Text' disabled={isReadOnly} />

              <WorkflowNumericField name='priority' label='Priority' placeholder='Number' disabled={isReadOnly} />

              <WorkflowTextField name='version' label='Version' placeholder='Free Text' disabled={isReadOnly} />

              <WorkflowTextArea name='condition' label='Condition' placeholder='Free text' rows={4} disabled={isReadOnly} />
            </form>
          </FormContainer>
        </div>

        {isEditMode && (
          <div className='flex gap-12 px-24 py-20 border-t border-neutral-20'>
            <Button variant='secondary' className='flex-1' onClick={handleCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant='primary' className='flex-1' onClick={handleSubmit(onSubmit)} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
